import { AlertCircle, FileText, Loader2, TrendingUp } from 'lucide-react';
import React from 'react';

import { Badge } from '@/components/ui/badge';

import type { IStoreIncomingMasterList } from '../../../api/IncomingInvoice.interface';

import TableBody from './TableBody';
import useInvoicesListColumns from './useColumns';
import useTable from './useTable';

type Props = {
  items: IStoreIncomingMasterList[];
  resetFilters: () => void;
  isLoading: boolean;
  selectedInvoice: number | null;
  setSelectedInvoice: (invoiceId: number) => void;
};

function InvoicesIncomingTable({
  items,
  resetFilters,
  isLoading,
  selectedInvoice,
  setSelectedInvoice,
}: Props) {
  const tableContainerRef = React.useRef<HTMLDivElement>(null!);

  const columns = useInvoicesListColumns();

  const table = useTable({
    items,
    columns,
  });

  // Loading State
  if (isLoading) {
    return (
      <div className="flex h-[70vh] flex-col items-center justify-center rounded-3xl bg-gradient-to-br from-white/95 to-slate-50/95 p-16 shadow-2xl backdrop-blur-sm dark:from-slate-900/95 dark:to-slate-800/95">
        <div className="relative mb-10">
          {/* Loading animation container */}
          <div className="relative flex h-32 w-32 items-center justify-center">
            {/* Background glow */}
            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 opacity-40 blur-3xl"></div>

            {/* Main loading spinner */}
            <div className="relative flex h-24 w-24 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 shadow-2xl">
              <Loader2 className="h-12 w-12 animate-spin text-white" />
            </div>

            {/* Floating elements */}
            <div className="absolute -right-3 -top-3 flex h-8 w-8 items-center justify-center rounded-full bg-emerald-500 shadow-xl">
              <FileText className="h-4 w-4 text-white" />
            </div>
            <div className="absolute -bottom-3 -left-3 flex h-8 w-8 items-center justify-center rounded-full bg-amber-500 shadow-xl">
              <TrendingUp className="h-4 w-4 text-white" />
            </div>
          </div>
        </div>

        <div className="text-center">
          <h3 className="mb-4 text-3xl font-bold text-slate-900 dark:text-slate-100">
            جاري تحميل الفواتير
          </h3>
          <p className="text-xl text-slate-600 dark:text-slate-400">
            يرجى الانتظار بينما نقوم بجلب البيانات...
          </p>
        </div>

        {/* Progress indicator */}
        <div className="mt-10 flex items-center gap-4">
          <div className="h-3 w-24 animate-pulse rounded-full bg-gradient-to-r from-blue-500 to-indigo-600"></div>
          <div
            className="h-3 w-20 animate-pulse rounded-full bg-gradient-to-r from-indigo-500 to-purple-600"
            style={{ animationDelay: '0.2s' }}
          ></div>
          <div
            className="h-3 w-16 animate-pulse rounded-full bg-gradient-to-r from-purple-500 to-pink-600"
            style={{ animationDelay: '0.4s' }}
          ></div>
        </div>
      </div>
    );
  }

  // Empty State
  if (!items || items.length === 0) {
    return (
      <div className="flex h-[70vh] flex-col items-center justify-center rounded-3xl bg-gradient-to-br from-white/95 to-slate-50/95 p-16 shadow-2xl backdrop-blur-sm dark:from-slate-900/95 dark:to-slate-800/95">
        <div className="relative mb-10">
          {/* Empty state icon container */}
          <div className="relative flex h-40 w-40 items-center justify-center">
            {/* Background glow */}
            <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-slate-400 to-slate-600 opacity-40 blur-3xl"></div>

            {/* Main icon */}
            <div className="relative flex h-32 w-32 items-center justify-center rounded-3xl bg-gradient-to-br from-slate-500 to-slate-700 shadow-2xl">
              <AlertCircle className="h-16 w-16 text-white" />
            </div>

            {/* Floating elements */}
            <div className="absolute -right-4 -top-4 flex h-10 w-10 items-center justify-center rounded-full bg-amber-500 shadow-xl">
              <FileText className="h-5 w-5 text-white" />
            </div>
            <div className="absolute -bottom-4 -left-4 flex h-10 w-10 items-center justify-center rounded-full bg-rose-500 shadow-xl">
              <TrendingUp className="h-5 w-5 text-white" />
            </div>
          </div>
        </div>

        <div className="text-center">
          <h3 className="mb-6 text-4xl font-bold text-slate-900 dark:text-slate-100">
            لا توجد فواتير وارد
          </h3>
          <p className="mb-10 max-w-2xl text-xl text-slate-600 dark:text-slate-400">
            لم يتم العثور على أي فواتير وارد في النظام. يمكنك تغيير معايير البحث
            أو إضافة فواتير جديدة.
          </p>

          {/* Action buttons */}
          <div className="flex flex-col gap-6 sm:flex-row sm:justify-center">
            <Badge
              variant="outline"
              className="border-blue-200 bg-blue-50 px-6 py-3 text-lg text-blue-700 dark:border-blue-700 dark:bg-blue-950/30 dark:text-blue-300"
            >
              <FileText className="mr-3 h-5 w-5" />
              فواتير جديدة
            </Badge>
            <Badge
              variant="outline"
              className="border-emerald-200 bg-emerald-50 px-6 py-3 text-lg text-emerald-700 dark:border-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300"
            >
              <TrendingUp className="mr-3 h-5 w-5" />
              تحديث البيانات
            </Badge>
          </div>
        </div>
      </div>
    );
  }

  // Main Table - Card Based Design
  return (
    <div className="space-y-8">
      {/* Table Content */}
      <div className="rounded-3xl bg-gradient-to-br from-white/90 to-slate-50/90 p-0 shadow-2xl backdrop-blur-sm dark:from-slate-900/90 dark:to-slate-800/90">
        <div className="overflow-hidden rounded-2xl border border-slate-200/50 bg-white/50 shadow-xl backdrop-blur-sm dark:border-slate-700/50 dark:bg-slate-800/50">
          <TableBody
            tableContainerRef={tableContainerRef}
            columns={columns}
            items={items}
            table={table}
            resetFilters={resetFilters}
            selectedInvoice={selectedInvoice}
            setSelectedInvoice={setSelectedInvoice}
          />
        </div>
      </div>
    </div>
  );
}

export default InvoicesIncomingTable;
